import { useQueryIntegrationLinkSessionInfo } from '@/hooks/query/integrations/general/link/useQueryIntegrationLinkSessionInfo';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';

/**
 * Resolves the session's EHR provider using a three-tier priority chain:
 * 1. Linked appointment provider — most authoritative; reflects which integration
 *    was actually used to book this appointment (e.g. Genie Web via the agent).
 * 2. session.ehr_provider — set by the backend at session creation.
 * 3. patient_profile.ehr — legacy patient EHR profile; used as last resort only,
 *    as it may be stale (e.g. desktop Genie on a patient who now books via Genie Web).
 *
 * This order is the single source of truth — any hook that needs sessionEhrProvider
 * should call this instead of re-implementing the chain.
 */
export function useSessionEhrProvider(sessionId: string) {
  const { data: sessionData, isLoading: isSessionLoading } = useQuerySessionById({
    sessionId,
    select: (data) => ({
      ehr_provider: data?.ehr_provider as EHRProvider | undefined,
      patient_ehr: data?.patient_profile?.ehr as EHRProvider | undefined,
    }),
  });
  const { data: linkedSessionInfo, isLoading: isLinkedSessionInfoLoading } =
    useQueryIntegrationLinkSessionInfo({ sessionId });

  const sessionEhrProvider =
    (linkedSessionInfo?.provider_appointment_details.provider as EHRProvider) ??
    sessionData?.ehr_provider ??
    sessionData?.patient_ehr;

  // 422 from the link-info endpoint is cached as `data: null` (see hook),
  // so derive "has a link" from data presence rather than isSuccess.
  const isLinkedSessionInfoSuccess = Boolean(linkedSessionInfo?.id);

  // True while either query that feeds the provider is still fetching. Callers
  // that must never offer an Auto affordance to an EHR-linked session can treat
  // "pending" as "assume EHR" so Auto isn't briefly shown before the
  // linked-appointment tier resolves.
  const isEhrProviderPending = isSessionLoading || isLinkedSessionInfoLoading;

  return { sessionEhrProvider, isLinkedSessionInfoSuccess, isEhrProviderPending };
}
